import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { adminAPI } from '../../api';
import { useToast } from '../../hooks/useToast';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { AlertTriangle, ChevronRight, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'rejected', label: 'Rejected' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'All severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

function truncate(str, maxLen = 60) {
  if (!str) return '';
  return str.length <= maxLen ? str : str.slice(0, maxLen) + '…';
}

function SeverityBadge({ severity }) {
  const variants = {
    critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    low: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };
  return (
    <Badge className={variants[severity] || variants.low}>
      {severity}
    </Badge>
  );
}

function StatusBadge({ status }) {
  const variants = {
    new: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    investigating: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    rejected: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };
  return (
    <Badge className={variants[status] || variants.new}>
      {status}
    </Badge>
  );
}

export default function AdminErrors() {
  const navigate = useNavigate();
  const { error: toastError, ToastContainer } = useToast();
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [severityFilter, setSeverityFilter] = useState('');
  const [routeFilter, setRouteFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  const fetchGroups = async () => {
    try {
      setLoading(true);
      setListError('');
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (statusFilter) params.status = statusFilter;
      else if (!includeClosed) params.excludeStatus = 'resolved,rejected';
      if (severityFilter) params.severity = severityFilter;
      if (routeFilter) params.route = routeFilter;
      if (searchFilter) params.search = searchFilter;
      if (dateFromFilter) params.dateFrom = dateFromFilter;
      if (dateToFilter) params.dateTo = dateToFilter;
      const data = await adminAPI.getErrorGroups(params);
      setGroups(data.groups || []);
      setPagination((prev) => ({
        ...prev,
        ...(data.pagination || {}),
      }));
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data;
      let message = body?.error || body?.message || err?.message || 'Failed to load error groups';
      if (status === 403) {
        message = body?.error || 'You need global admin access to view errors.';
      }
      if (status === 503 && body?.code === 'SCHEMA_OUTDATED') {
        message = body.error || 'Database schema is outdated. Run error logging migrations.';
      }
      setListError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [pagination.page, statusFilter, includeClosed, severityFilter, routeFilter, searchFilter, dateFromFilter, dateToFilter]);

  // Update last_seen_at when admin visits /admin/errors
  useEffect(() => {
    adminAPI.markErrorsSeen().catch(() => {
      // Silently ignore - user may not have permission
    });
  }, []);

  const handleMarkSeen = async () => {
    try {
      await adminAPI.markErrorsSeen();
      fetchGroups();
    } catch (err) {
      toastError('Failed', err?.message || 'Could not mark as seen.');
    }
  };

  return (
    <Layout>
      <ToastContainer />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Error Groups</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            View and manage application error tickets. Resolved and rejected errors are hidden by default—check &quot;Include closed&quot; to see them.
          </p>
        </div>

        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <CardHeader className="border-b border-gray-200 dark:border-gray-700">
            <CardTitle className="text-lg text-gray-900 dark:text-gray-100">Errors</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="w-40">
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="w-40">
                <Select
                  label="Severity"
                  value={severityFilter}
                  onChange={(e) => {
                    setSeverityFilter(e.target.value);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {SEVERITY_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="w-48">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Route search
                </label>
                <Input
                  type="text"
                  value={routeFilter}
                  onChange={(e) => {
                    setRouteFilter(e.target.value);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  placeholder="Filter by route"
                  className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="w-48">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Text search
                </label>
                <Input
                  type="text"
                  value={searchFilter}
                  onChange={(e) => {
                    setSearchFilter(e.target.value);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  placeholder="Title or fingerprint"
                  className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="w-36">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  From date
                </label>
                <input
                  type="date"
                  value={dateFromFilter}
                  onChange={(e) => {
                    setDateFromFilter(e.target.value);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="w-36">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  To date
                </label>
                <input
                  type="date"
                  value={dateToFilter}
                  onChange={(e) => {
                    setDateToFilter(e.target.value);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeClosed}
                  onChange={(e) => {
                    setIncludeClosed(e.target.checked);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Include closed</span>
              </label>
              <div className="flex items-end">
                <Button variant="outline" onClick={handleMarkSeen} size="sm">
                  Mark all seen
                </Button>
              </div>
            </div>

            {listError && (
              <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                {listError}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : groups.length === 0 ? (
              <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                <AlertTriangle className="mx-auto h-10 w-10 mb-2 opacity-50" />
                <p>No error groups match your filters.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Severity
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Count
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Last seen
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Route
                        </th>
                        <th className="px-4 py-2 w-10" aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {groups.map((group) => (
                        <tr
                          key={group.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 max-w-xs">
                            {truncate(group.title || group.fingerprint, 70)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <SeverityBadge severity={group.severity} />
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <StatusBadge status={group.status} />
                            {group.isMuted && (
                              <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">(muted)</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                            {group.countTotal ?? group.count_total ?? 0}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {group.lastSeen || group.last_seen
                              ? format(new Date(group.lastSeen || group.last_seen), 'dd MMM yyyy HH:mm')
                              : '—'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 max-w-[200px] truncate" title={group.lastRoute || group.last_route}>
                            {group.lastRoute || group.last_route || '—'}
                          </td>
                          <td className="px-4 py-2">
                            <button
                              type="button"
                              onClick={() => navigate(`/admin/errors/${group.id}`)}
                              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
                              aria-label="View details"
                            >
                              <ChevronRight className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page <= 1}
                        onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page >= pagination.totalPages}
                        onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
